import { makeItem } from '@/ui/components/inspectionReport/items';
import { isOnFreePlan as isAccountOnFreePlan } from '@/ui/auth/auth';
import { computed, defineComponent, h, provide, watch } from 'vue';
import Attachment from '@/ui/components/inspectionReport/items/Attachment';
import { t } from '@/ui/i18n/i18n';
import { LegacyFile, LegacyInspection, LegacyItem, LegacyPhoto } from '@/ui/components/inspectionReport/legacyTypes';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import Approval from '@/ui/components/inspectionReport/items/Approval';
import { ResponsesTypes } from '@/entities/checklist';
import { isLowQualityImagesFromReportPageKey } from '@/ui/injectionKeys';
import { SelectOptions } from '@/ui/types';

export default defineComponent({
  name: 'InspectionReport',
  props: {
    resource: {
      type: Object as () => LegacyInspection,
      required: true,
    },
    isLowQualityImages: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array as () => SelectOptions,
      default: () => [],
    },
    isTranslationEnabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    provide(isLowQualityImagesFromReportPageKey, props.isLowQualityImages);
    const { resource, categories, isTranslationEnabled } = useInspectionReport();
    resource.value = props.resource;
    categories.value = props.categories;
    isTranslationEnabled.value = props.isTranslationEnabled;

    watch(
      () => props.resource,
      () => {
        resource.value = props.resource;
      },
      { deep: true },
    );

    watch(() => props.categories, () => {
      categories.value = props.categories;
    }, { deep: true });

    const isPhotoResponse = (item: LegacyItem) => item.response_type === ResponsesTypes.photo;
    const isAnnotationResponse = (item: LegacyItem) => item.response_type === ResponsesTypes.annotation;
    const isListOrEntityResponseAndHasPhotos = (item: LegacyItem) => [
      ResponsesTypes.entityItem,
      ResponsesTypes.list,
      ResponsesTypes.file,
    ].includes(item.response_type as ResponsesTypes) && item.photos && item.photos.length > 0;
    const isConditionalSection = (item: LegacyItem) => item.type === 'conditional_section';
    const hasAllConditionsMet = (item: LegacyItem) => Object.prototype.hasOwnProperty.call(item, 'allConditionsMet');

    const getAttachments = (source: Array<LegacyItem>, params: { index: number }) => {
      const items: Array<LegacyPhoto> = [];
      // @ts-ignore: Not all code paths return a value
      source?.forEach((currentItem: LegacyItem) => { // eslint-disable-line consistent-return
        if (currentItem.items) {
          if (isConditionalSection(currentItem) && hasAllConditionsMet(currentItem) && !currentItem.allConditionsMet) {
            return items;
          }

          items.push(...getAttachments(currentItem.items, params));
        } else if (isPhotoResponse(currentItem) && currentItem.responded && currentItem.response
          && Array.isArray(currentItem.response)) {
          items.push(...((currentItem.response as Array<LegacyPhoto>).map((photo: LegacyPhoto) => {
            photo.index = params.index++;

            return photo;
          })));
        } else if (isAnnotationResponse(currentItem) && currentItem.responded && currentItem.response) {
          currentItem.index = params.index++;

          items.push({
            ...(currentItem.response as LegacyPhoto),
            index: currentItem.index,
            // We need to keep this image binary reference for pdf
            image: currentItem.params.image!,
            image_id: currentItem.params.image_id!,
          });
        } else if (isListOrEntityResponseAndHasPhotos(currentItem)) {
          items.push(...(currentItem.photos!.map((photo: LegacyPhoto) => {
            photo.index = params.index++;
            return photo;
          })));
        }
      });

      return items;
    };

    const getFileAttachments = (source: Array<LegacyItem>) => {
      const items: Array<LegacyPhoto> = [];
      source?.forEach((currentItem: LegacyItem) => {
        if (currentItem.items) {
          items.push(...getFileAttachments(currentItem.items));
        } else if (currentItem.response_type === 'file' && currentItem.responded && currentItem.response) {
          items.push(...(currentItem.response as Array<LegacyFile>).map((photo: LegacyFile) => {
            return photo;
          }));
        }
      });

      return items;
    };

    const logoPath = computed(() => {
      const logoFile = '/lumiform.svg';
      return process.env.VUE_APP_BUILD_MODE === 'pdf'
        ? `${process.env.VUE_APP_ADMIN_URL}${logoFile}`
        : logoFile;
    });

    const attachments = computed(() => getAttachments(props.resource.form?.items ?? [], { index: 1 }));
    const fileAttachments = computed(() => getFileAttachments(props.resource.form?.items ?? []));

    const getTitle = () => {
      if (props.resource.title) {
        return props.resource.title;
      }

      const showTranslated = isTranslationEnabled.value && props.resource.checklist_title_translated;

      return showTranslated
        ? props.resource.checklist_title_translated
        : props.resource.checklist_title;
    };

    const getDescription = () => {
      if (!props.resource.template?.display_description) {
        return null;
      }

      const showTranslated = isTranslationEnabled.value && props.resource.checklist_description_translated;

      return h('p', [
        showTranslated
          ? props.resource.checklist_description_translated
          : props.resource.checklist_description,
      ]);
    };

    return function render() {
      return h('div', { class: ['inspection-report'] }, [
        h('div', { class: ['header', 'flex', 'mb-6'] }, [
          h('div', {
            class: 'flex-1 inspection-report__header-container',
          }, [
            h('span', { class: 'inspection-report__header-title' }, [getTitle()]),
            h('div', { class: 'inspection-report__header-description' }, [getDescription()]),
          ]),
          isAccountOnFreePlan.value
            ? h('div', { class: 'export-inspection-logo-container' }, [
              h('div', { class: ['font-barlow', 'mb-2', 'text-right'] }, [t('inspection-report.powered-by')]),
              h('img', {
                attrs: {
                  class: 'export-inspection-logo',
                  src: logoPath.value,
                },
              }),
            ])
            : null,
          props.resource.template?.logo
            ? h('div', [
              h('img', {
                attrs: {
                  class: 'inspection-report__header-logo',
                  src: props.resource.template.logo,
                },
              }),
            ])
            : null,
        ]),
        makeItem(props.resource.form),
        props.resource.approval
          ? h(Approval, {
            props: {
              approval: props.resource.approval,
            },
          })
          : null,
        (attachments.value && attachments.value.length > 0)
        || (fileAttachments.value && fileAttachments.value.length > 0)
          ? h(Attachment, {
            props: {
              attachments: attachments.value,
              files: fileAttachments.value,
            },
          })
          : null,
      ]);
    };
  },
});
